import type { SnackbarMessage, OptionsObject, SnackbarKey } from 'notistack';

interface RequestParams {
  path: string;
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
}

const getAccessToken = () => {
  const item = window.localStorage.getItem('accessToken') || '';
  return item ? JSON.parse(item) : '';
};

export const httpRequest = async ({ path, body = {}, method }: RequestParams) => {
  const accessToken = getAccessToken();

  try {
    const response = await fetch(path, {
      method,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (response.status === 202) {
      return { data: null, response, error: false };
    }

    const respBody = await response.text(); // safe for empty bodies
    const data = (respBody.length && JSON.parse(respBody)) || {};

    return { data, response, error: !response.ok };
  } catch (err) {
    console.log(`Error in HTTP ${method}:`, err);
    return { data: null, response: null, error: true };
  }
};

interface UploadRequestParams {
  path: string;
  file: File;
  /** The multipart field name the server expects. */
  fieldName?: string;
}

/**
 * POSTs a single file as multipart/form-data. Unlike httpRequest, no Content-Type
 * header is set so the browser supplies the multipart boundary itself.
 */
export const httpUploadRequest = async ({ path, file, fieldName = 'file' }: UploadRequestParams) => {
  const accessToken = getAccessToken();

  const formData = new FormData();
  formData.append(fieldName, file, file.name);

  try {
    const response = await fetch(path, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });

    const respBody = await response.text(); // safe for empty bodies
    // Parse defensively: a resolved HTTP response may carry a non-JSON body (e.g. an ingress/proxy
    // HTML page on a 413). That must NOT be treated as a network failure — we still return the
    // response so callers can branch on response.status (413 size limit, 409 duplicate, etc.).
    // Response bodies vary by upload endpoint and are narrowed by the caller using response.status.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let data: any = {};
    if (respBody.length) {
      try {
        data = JSON.parse(respBody);
      } catch {
        data = {};
      }
    }

    return { data, response, error: false };
  } catch (err) {
    console.log('Error in HTTP upload:', err);
    return { data: null, response: null, error: true };
  }
};

interface sendHttpRequestParams {
  path: RequestParams['path'];
  method: RequestParams['method'];
  body?: RequestParams['body'];
  successMessage: SnackbarMessage;
  errorMessage: SnackbarMessage;
  enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey;
  successOptions?: OptionsObject;
  errorOptions?: OptionsObject;
}

export async function sendHttpRequest({
  path,
  method,
  body,
  successMessage,
  errorMessage,
  enqueueSnackbar,
  successOptions,
  errorOptions,
}: sendHttpRequestParams) {
  const result = await httpRequest({ path, method, body });
  const { error } = result;
  if (error) {
    enqueueSnackbar(errorMessage, { variant: 'error', ...errorOptions });
  } else {
    enqueueSnackbar(successMessage, { variant: 'success', ...successOptions });
  }

  return result;
}
